import { throttle } from 'lodash-es';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
/**
 * The interval, in milliseconds, at which pointer moves are handled while dragging.
 */
const RESIZE_THROTTLE_MS = 50;
/**
 * Hook providing drag-to-resize behavior for a side panel. It tracks the pointer on
 * the document so the drag survives the pointer leaving the handle, and clamps the
 * resulting width between {@code minWidth} and {@code maxWidth}.
 *
 * @param {IPanelResizeOptions} options - The panel specific resize options.
 * @returns {IPanelResize}
 */
export default function usePanelResize({ edge, maxWidth, minWidth, setIsResizing, setWidth, width }) {
    const dispatch = useDispatch();
    const [isMouseDown, setIsMouseDown] = useState(false);
    const [mousePosition, setMousePosition] = useState(null);
    const [dragWidth, setDragWidth] = useState(null);
    /**
     * Handles pointer down on the drag handle.
     * Supports both mouse and touch events via Pointer Events API.
     *
     * @param {React.PointerEvent} e - The pointer down event.
     * @returns {void}
     */
    const onDragHandlePointerDown = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        // Capture the pointer to ensure we receive all pointer events
        // even if the pointer moves outside the element.
        e.target.setPointerCapture(e.pointerId);
        // Store the initial pointer position and panel width.
        setIsMouseDown(true);
        setMousePosition(e.clientX);
        setDragWidth(width);
        dispatch(setIsResizing(true));
        // Add visual feedback that we're dragging (cursor for mouse, not visible on touch)
        // and disable text selection during resize.
        document.body.style.cursor = 'col-resize';
        document.body.style.userSelect = 'none';
    }, [width, setIsResizing, dispatch]);
    /**
     * Handles pointer up to end drag resize.
     *
     * @returns {void}
     */
    const onDragPointerUp = useCallback(() => {
        if (isMouseDown) {
            setIsMouseDown(false);
            dispatch(setIsResizing(false));
            // Restore cursor and text selection.
            document.body.style.cursor = '';
            document.body.style.userSelect = '';
        }
    }, [isMouseDown, setIsResizing, dispatch]);
    /**
     * Handles pointer move during drag resize.
     *
     * @param {PointerEvent} e - The pointermove event.
     * @returns {void}
     */
    const onPointerMove = useCallback(throttle((e) => {
        if (isMouseDown && mousePosition !== null && dragWidth !== null) {
            const diff = e.clientX - mousePosition;
            const draggedWidth = edge === 'right' ? dragWidth + diff : dragWidth - diff;
            const newWidth = Math.max(Math.min(draggedWidth, maxWidth), minWidth);
            // Update the width only if it has changed.
            if (newWidth !== width) {
                dispatch(setWidth(newWidth));
            }
        }
    }, RESIZE_THROTTLE_MS, {
        leading: true,
        trailing: false
    }), [isMouseDown, mousePosition, dragWidth, width, maxWidth, minWidth, edge, setWidth, dispatch]);
    // Set up global event listeners for drag tracking.
    useEffect(() => {
        document.addEventListener('pointerup', onDragPointerUp);
        document.addEventListener('pointermove', onPointerMove);
        return () => {
            document.removeEventListener('pointerup', onDragPointerUp);
            document.removeEventListener('pointermove', onPointerMove);
        };
    }, [onDragPointerUp, onPointerMove]);
    return {
        isMouseDown,
        onDragHandlePointerDown
    };
}
